import { Control, Controller } from 'react-hook-form'
import { KeyboardEvent, useState } from 'react'
import TextField from '@mui/material/TextField'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import Tooltip from '@mui/material/Tooltip'
import { faInfinity } from '@fortawesome/free-solid-svg-icons/faInfinity'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import { useAppDispatch } from '../../../shared/lib/hooks/useAppDispatch/useAppDispatch'
import { useAppSelector } from '../../../shared/lib/hooks/useAppSelector/useAppSelector'
import {
	isExpirationUnlimited,
	LICENSE_UNLIMITED,
	TEXT_UNLIMITED
} from '../../../entities/License'
import { selectExpirationDate } from '../model/selectors/selectExpirationDate'
import { newApplicationActions } from '../model/slices/newApplicationSlice'
import { localAsUTC } from '../../../shared/lib/DateAndTieHelpers/localAsUTC'
import { SvgIcon } from '../../../shared/ui/SvgIcon/SvgIcon'
import { SxProps } from '@mui/material'

interface AppExpirationDateProps {
	control?: Control
	styles?: SxProps,
}

export const AppExpirationDate = (props: AppExpirationDateProps) => {
	const { control, styles } = props
	const dispatch = useAppDispatch()

	const [open, setOpen] = useState(false)

	const expirationDate = useAppSelector(selectExpirationDate)
	const isExpirationDateUnlimited = isExpirationUnlimited(expirationDate)

	const handleOpen = () => setOpen(true)
	const handleClose = () => setOpen(false)

	const handleDateChange = (newValue: Date | null) => {
		if (newValue) {
			dispatch(newApplicationActions.setExpirationDate(localAsUTC(newValue)))
		}
	}

	const handleKeyDown = (e: KeyboardEvent) => {
		e.preventDefault()
	}

	const handleMakeUnlimited = () => {
		dispatch(newApplicationActions.setExpirationDate(localAsUTC(new Date(LICENSE_UNLIMITED))))
	}

	return (
		<Stack direction="row" spacing={2} alignItems={'baseline'} sx={styles}>
			<LocalizationProvider dateAdapter={AdapterDateFns}>
				<Controller
					name="expirationDate"
					control={control}
					defaultValue={expirationDate}
					render={({ field }) => {
						return (
							<DatePicker
								open={open}
								onOpen={handleOpen}
								onClose={handleClose}
								disablePast
								inputFormat="yyyy-MM-dd"
								label="Application expiration date"
								value={expirationDate || null}
								onChange={(date) => {
									field.onChange(date)
									handleDateChange(date)
								}}
								PopperProps={{
									placement: 'bottom-start'
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										fullWidth
										required
										onKeyDown={handleKeyDown}
										onClick={handleOpen}
										InputLabelProps={{ shrink: !!expirationDate }}
										inputProps={{
											...params.inputProps,
											readOnly: true,
											placeholder:
												'Pick application expiration date  \u279C'
										}}
										helperText={isExpirationDateUnlimited && TEXT_UNLIMITED}
									/>
								)}
							/>
						)
					}}
				/>
			</LocalizationProvider>
			<Tooltip title={'Make unlimited'}>
				<Button
					variant="outlined"
					sx={{ px: 0, maxHeight: '2rem', minWidth: '32px' }}
					onClick={handleMakeUnlimited}
				>
					<SvgIcon icon={faInfinity} />
				</Button>
			</Tooltip>
		</Stack>
	)
}
